import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { MENU_TRAI, MENU_PHAI } from "../data/dieuHuong.js";
import { coDauDaDangNhap } from "../lib/auth.js";

/**
 * Đầu trang kiểu "logo ở giữa" (10/2026, lấy cảm hứng từ ycombinator.com):
 * ba mục bên trái, logo F ở giữa, ba mục bên phải, rồi Đăng nhập và nút
 * Nhận xét deck ở mép phải. Danh sách mục nằm ở src/data/dieuHuong.js.
 *
 * Dropdown mở bằng CSS (:hover, :focus-within) nên các liên kết con luôn có
 * trong HTML render sẵn cho Google. Trên điện thoại (dưới 1120px) đầu trang
 * thu lại: logo bên trái, nút Nhận xét deck và nút Menu bên phải; bấm Menu
 * mở bảng liệt kê đủ mọi mục và mục con.
 */

function LienKet({ m, className, onClick }) {
  if (m.href) {
    return (
      <a className={className} href={m.href} rel="noopener" onClick={onClick}>
        {m.nhan}
        {m.mo && <small>{m.mo}</small>}
      </a>
    );
  }
  return (
    <Link className={className} to={m.to} onClick={onClick}>
      {m.nhan}
      {m.mo && <small>{m.mo}</small>}
    </Link>
  );
}

function MucChinh({ m }) {
  const { pathname } = useLocation();
  // Mục cha sáng khi đang ở bất kỳ trang con nào của nó.
  // Mục con nằm sâu trong mục khác (vd. /cong-cu/quy-phu-hop dưới Nguồn vốn)
  // chỉ tính khi trùng đúng trang, để không sáng hai mục cùng lúc.
  const trong = (u) => pathname === u || pathname.startsWith(u + "/");
  const dangO = trong(m.to) || (m.con || []).some((c) =>
    c.to && (c.to.split("/").length === 2 ? trong(c.to) : pathname === c.to));
  if (!m.con) {
    return (
      <NavLink className="dt-muc" to={m.to}>
        {m.nhan}
      </NavLink>
    );
  }
  return (
    <div className="dt-nhom">
      <Link className={`dt-muc${dangO ? " active" : ""}`} to={m.to} aria-haspopup="true">
        {m.nhan}
        <svg className="dt-mui" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1l4 4 4-4" />
        </svg>
      </Link>
      <div className="dt-tha">
        <div className="dt-tha-trong">
          {m.con.map((c) => (
            <LienKet key={c.nhan} m={c} className="dt-con" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DauTrang() {
  const [mo, setMo] = useState(false);
  const [daDangNhap, setDaDangNhap] = useState(false);
  const { pathname } = useLocation();

  // Đọc sau khi chạy trên máy người dùng: HTML render sẵn luôn là "Đăng nhập".
  useEffect(() => {
    const doc = () => setDaDangNhap(coDauDaDangNhap());
    doc();
    window.addEventListener("fr-dang-nhap", doc);
    window.addEventListener("storage", doc);
    return () => {
      window.removeEventListener("fr-dang-nhap", doc);
      window.removeEventListener("storage", doc);
    };
  }, []);

  useEffect(() => setMo(false), [pathname]);

  useEffect(() => {
    if (!mo) return;
    const dong = (e) => e.key === "Escape" && setMo(false);
    document.addEventListener("keydown", dong);
    return () => document.removeEventListener("keydown", dong);
  }, [mo]);

  const taiKhoan = daDangNhap
    ? { nhan: "Bản chuẩn bị", to: "/ban-chuan-bi" }
    : { nhan: "Đăng nhập", to: "/ban-chuan-bi/tai-khoan" };

  return (
    <header className={`dt${mo ? " dt-dang-mo" : ""}`}>
      <div className="dt-vo">
        <nav className="dt-trai" aria-label="Điều hướng chính, phần trái">
          {MENU_TRAI.map((m) => <MucChinh key={m.nhan} m={m} />)}
        </nav>

        <Link to="/" className="dt-logo" aria-label="fundraising.vn, về trang chủ">
          <img src="/favicon.svg" width="40" height="40" alt="" />
          <span className="dt-logo-chu">fundraising<span>.vn</span></span>
        </Link>

        <nav className="dt-phai" aria-label="Điều hướng chính, phần phải">
          {MENU_PHAI.map((m) => <MucChinh key={m.nhan} m={m} />)}
        </nav>

        <div className="dt-hanh-dong">
          <NavLink className="dt-dang-nhap" to={taiKhoan.to}>
            {taiKhoan.nhan}
          </NavLink>
          <NavLink className="dt-nut" to="/nhan-xet-deck">
            Nhận xét deck
          </NavLink>
          <button
            type="button"
            className="dt-menu"
            aria-expanded={mo}
            aria-controls="dt-bang"
            onClick={() => setMo(!mo)}
          >
            <span className="sr-only">{mo ? "Đóng menu" : "Mở menu"}</span>
            <svg viewBox="0 0 20 14" aria-hidden="true">
              {mo ? <path d="M4 1l12 12M16 1L4 13" /> : <path d="M1 1h18M1 7h18M1 13h18" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Bảng menu trên điện thoại. Luôn có trong HTML để công cụ tìm kiếm
          đọc được, chỉ hiện khi bấm nút Menu. */}
      <nav id="dt-bang" className="dt-bang" aria-label="Menu đầy đủ">
        {[...MENU_TRAI, ...MENU_PHAI].map((m) => (
          <div className="dt-bang-nhom" key={m.nhan}>
            <Link className="dt-bang-cha" to={m.to}>{m.nhan}</Link>
            {m.con && (
              <div className="dt-bang-con">
                {m.con.filter((c) => c.to !== m.to).map((c) => (
                  <LienKet key={c.nhan} m={{ ...c, mo: undefined }} className="dt-bang-lk" />
                ))}
              </div>
            )}
          </div>
        ))}
        <Link className="dt-bang-cha" to={taiKhoan.to}>{taiKhoan.nhan}</Link>
      </nav>
    </header>
  );
}
